// providers/hooks/collect-story-fast-version-vars.ts
//
// Which fast-versioning variables does this deck actually use?
//
// This is the single implementation, shared by the publish flow
// (`useGetStoryFastVersionVars`, whose output becomes `settings.fs["fs-vars"]`)
// and the AI agent (`live-story-variables.ts`). The agent used to read
// `settings.fs["fs-vars"]` instead — a publish-time snapshot that misses any
// variable added since the last publish.
//
// Do not copy this logic to a third caller: divergence HERE means a
// personalized deck reaching a real customer with the wrong fields in it.
//
// The answer, in order:
//   1. A known-variable registry, first name wins: the org's backend variables
//      (or the static defaults when that list is empty — also what a trial plan
//      gets, the query is skipped there), the story's formulas, the Storydoc
//      catalogue, Fathom's, the last published snapshot, the account variables.
//   2. `collectStoryVariables` walks the deck: every identifier-shaped token is
//      a variable (a registry entry when there is one, otherwise synthesized),
//      plus list / data bindings and the operands of AI prompts and formulas.
//      Repeatable-group tokens are left to the repeatable feature.
//
// There is no plan filter: plan limits are enforced at publish / version
// create (the Upgrade-to-publish gate and the server), not by hiding a
// variable the deck renders anyway.

import type { StorySettingsType } from "@config";
import { accountVariables, fathomVariables } from "@store/fast-versions";
import { FAST_VERSION_VARIABLES } from "../rendering-provider/data";
import {
  collectStoryVariables,
  type KnownVariable,
} from "./collect-story-variables";
import { STORYDOC_KNOWN_VARIABLES } from "./storydoc-known-variables";

// A pool entry: the variable definition plus the plan gate that wraps it.
// Deliberately loose — these come from several sources with slightly
// different shapes.
export type FsVarPoolEntry = {
  plan?: string;
  archived?: boolean;
  variable: Record<string, any>;
};

export type CollectedFsVariable = Record<string, any> & {
  name: string;
  type?: string;
};

export type CollectStoryFastVersionVarsArgs = {
  // The deck's CONTENT, already un-flattened. Callers holding the editor's
  // normalized state must invert it first (`invertFlatData`) exactly as
  // use-publish.tsx does — redux's `story.story.slides` is a partial and does
  // not carry the live content.
  slides: unknown;
  // Story settings: the last published `fs-vars`, formulas, repeatables, og.
  settings: Record<string, any> | null | undefined;
  // The org's variables from `GET /fast-version`. Empty/undefined falls back to
  // the static defaults.
  backendVars: FsVarPoolEntry[] | undefined;
};

export const collectStoryFastVersionVars = ({
  slides,
  settings,
  backendVars,
}: CollectStoryFastVersionVarsArgs): CollectedFsVariable[] => {
  if (!settings) return [];

  const base =
    backendVars && backendVars.length ? backendVars : FAST_VERSION_VARIABLES;
  const knownVariables = [
    ...base,
    // Story-local formula (calc) variables carry the `formula` field the
    // runtime needs. Placed first among story sources so the definition
    // (with formula) wins for a calc token.
    ...getStoryCalcVariables({ settings }),
    // Predefined Storydoc catalog carries the real types (color, select,
    // media, …). Placed before the story's prior manifest so a token that
    // an earlier publish synthesized as `input` is corrected to its type.
    ...STORYDOC_KNOWN_VARIABLES,
    ...fathomVariables,
    ...getCurrentStoryVariables({ settings }).map(
      (v: Record<string, unknown>) => ({ variable: v }),
    ),
    ...accountVariables,
  ] as KnownVariable[];

  return collectStoryVariables({
    story: { slides, settings },
    knownVariables,
    repeatableKeys: getRepeatableKeys({ settings }),
  }) as CollectedFsVariable[];
};

/** The last published `fs-vars` snapshot; `[]` when absent or malformed. */
export const getCurrentStoryVariables = ({
  settings,
}: {
  settings?: StorySettingsType | Record<string, any> | null;
}) => {
  try {
    const fs = settings?.fs;
    if (!fs) return [];
    const variables = JSON.parse(fs["fs-vars"]);
    return Array.isArray(variables) ? variables : [];
  } catch {
    return [];
  }
};

/**
 * Story-local formula variables (settings.fs["calc-formulas"]) as
 * KnownVariable[] carrying `type: "calculation"` + `formula`, so publish bakes
 * the formula into the fs-vars manifest and the runtime can evaluate calc
 * pills. See docs/plans/2026-08-19-story-local-formulas-design.md.
 */
export const getStoryCalcVariables = ({
  settings,
}: {
  settings?: StorySettingsType | Record<string, any> | null;
}): KnownVariable[] => {
  try {
    const raw = (settings?.fs as Record<string, string> | undefined)?.[
      "calc-formulas"
    ];
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Record<
      string,
      { title?: string; formula?: string }
    >;
    if (!parsed || typeof parsed !== "object") return [];
    return Object.entries(parsed)
      .filter(([, def]) => def && typeof def.formula === "string")
      .map(([name, def]) => ({
        variable: {
          name,
          type: "calculation",
          title: def.title ?? name,
          formula: def.formula ?? "",
        },
      })) as unknown as KnownVariable[];
  } catch {
    return [];
  }
};

// Repeatable-namespaced tokens ({{<key>}} / {{<key>.<field>}}) are collected
// separately (see repeatable feature) and must be excluded from the generic
// fs-vars manifest so they don't leak into the share-form / default-values UI.
const getRepeatableKeys = ({
  settings,
}: {
  settings?: StorySettingsType | Record<string, any> | null;
}): string[] => {
  try {
    const raw = settings?.fs?.["repeatables"];
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((r: { key: string }) => r.key)
      : [];
  } catch {
    return [];
  }
};
